import React, { useState, useEffect, useCallback } from 'react';
import { ValentinesCardPage } from '@/features/valentines/pages/valentines-card-page';
import { DiaryPage } from '@/features/diary/pages/DiaryPage';
import { AdminLoginPage } from '@/features/admin/pages/AdminLoginPage';
import { AdminDashboardPage } from '@/features/admin/pages/AdminDashboardPage';

type TabId = 'walentynki' | 'pamietniczek';
type ViewId = 'main' | 'admin-login' | 'admin-dashboard';

const TabNavigation = ({
  activeTab,
  onTabChange,
  dark,
}: {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  dark: boolean;
}): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const getActiveClass = (tabId: TabId, isActive: boolean): string => {
    if (!isActive) {
      return dark ? 'text-gray-300 hover:bg-gray-800/50' : 'text-gray-700 hover:bg-gray-100/50';
    }
    if (tabId === 'walentynki') {
      return dark ? 'bg-rose-600/20 text-rose-300' : 'bg-rose-50 text-rose-600';
    }
    return dark ? 'bg-amber-600/20 text-amber-300' : 'bg-amber-50 text-amber-600';
  };

  return (
    <div className="fixed top-2 left-2 sm:top-4 sm:left-4 z-[100]">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 p-2 sm:px-4 sm:py-2 rounded-lg shadow-lg transition-all duration-300 ${
            dark
              ? 'bg-gray-900/90 backdrop-blur-md text-gray-100 hover:bg-gray-800/90'
              : 'bg-white/90 backdrop-blur-md text-gray-800 hover:bg-gray-50/90'
          }`}
        >
          {/* Hamburger icon on mobile */}
          <svg
            className="w-5 h-5 sm:hidden"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {/* Text on desktop */}
          <span className="hidden sm:block text-sm font-medium">
            {activeTab === 'walentynki' ? 'Walentynki' : 'Pamiętniczek'}
          </span>
          <svg
            className={`hidden sm:block w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0" onClick={() => setIsOpen(false)} />
            <div
              className={`absolute top-full left-0 mt-2 py-1 rounded-lg shadow-xl min-w-[140px] ${
                dark ? 'bg-gray-900/95 backdrop-blur-md' : 'bg-white/95 backdrop-blur-md'
              }`}
            >
              <button
                onClick={() => {
                  onTabChange('walentynki');
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${getActiveClass('walentynki', activeTab === 'walentynki')}`}
              >
                Walentynki
              </button>
              <button
                onClick={() => {
                  onTabChange('pamietniczek');
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${getActiveClass('pamietniczek', activeTab === 'pamietniczek')}`}
              >
                Pamiętniczek
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const App = (): React.JSX.Element => {
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = localStorage.getItem('activeTab');
    return (saved as TabId) || 'walentynki';
  });

  const [view, setView] = useState<ViewId>(() => {
    const path = window.location.pathname;
    if (path === '/admin') return 'admin-login';
    if (path === '/admin/dashboard') return 'admin-dashboard';
    return 'main';
  });

  const [dark, setDark] = useState(false);

  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDark(prefersDark);
  }, []);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (): void => {
      const path = window.location.pathname;
      if (path === '/admin') setView('admin-login');
      else if (path === '/admin/dashboard') setView('admin-dashboard');
      else setView('main');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToAdmin = useCallback(() => {
    window.history.pushState({}, '', '/admin');
    setView('admin-login');
  }, []);

  const navigateToAdminDashboard = useCallback(() => {
    window.history.pushState({}, '', '/admin/dashboard');
    setView('admin-dashboard');
  }, []);

  const navigateToMain = useCallback(() => {
    window.history.pushState({}, '', '/');
    setView('main');
  }, []);

  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
      setView('main');
    }
  }, []);

  // Render admin views
  if (view === 'admin-login') {
    return (
      <AdminLoginPage
        dark={dark}
        onLoginSuccess={navigateToAdminDashboard}
        onBack={navigateToMain}
      />
    );
  }

  if (view === 'admin-dashboard') {
    return (
      <AdminDashboardPage
        dark={dark}
        onLogout={navigateToAdmin}
        onBack={navigateToMain}
      />
    );
  }

  // Render main view with tabs
  return (
    <>
      <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} dark={dark} />
      {activeTab === 'walentynki' ? (
        <ValentinesCardPage />
      ) : (
        <DiaryPage dark={dark} onAdminClick={navigateToAdmin} />
      )}
    </>
  );
};

export default App;
